import { NgClass } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { environment } from '@siat-fe/env';
import { take } from 'rxjs';
import { Widget, WidgetID } from '../models';
import { getWidgetHeaderComponentTitleId } from '../models/widget.utils';

@Component({
  selector: 'siat-widget-header',
  standalone: true,
  imports: [
    NgClass,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatTooltipModule,
    TranslatePipe,
  ],
  templateUrl: './widget-header.component.html',
  styleUrl: './widget-header.component.scss',
})
export class WidgetHeaderComponent {
  private router = inject(Router);
  private httpClient = inject(HttpClient);
  private translate = inject(TranslateService);
  @Input() lockedPatient: boolean;
  @Input() widget!: Widget;
  @Input() totalCount = 0;
  @Input() showCreateButton = true;
  @Input() hasCreatePermission = true;
  @Input() hasReadPermission = true;
  @Input() showStoricoRecapiti = false;

  @Output() create = new EventEmitter<void>();
  @Output() refreshConsensiAsuit = new EventEmitter<void>();
  @Output() openStoricoRecapiti = new EventEmitter<void>();

  isLoadingChangeContext = signal(false);

  get showViewAllCount(): boolean {
    return ![WidgetID.PRESCRIZIONI, WidgetID.DOCUMENTI_DSE].includes(
      this.widget?.id as WidgetID
    );
  }

  /**
   * Accessible name for the "view all" link, including the result count
   * when it's visibly shown, so it matches the rendered text.
   * @returns {string} The label text.
   */
  get viewAllLabel(): string {
    const label = this.translate.instant('GENERAL.viewAll');
    if (this.showViewAllCount && this.widget.id !== 'parametri') {
      return `${label} (${this.totalCount})`;
    }
    return label;
  }

  /**
   * Builds the title id, avoiding a collision with widget-base's own
   * (hidden but still rendered) title for the same widget types.
   * @returns {string} The heading element id.
   */
  get titleId(): string {
    return getWidgetHeaderComponentTitleId(this.widget.id);
  }

  getAllergyChipClass(level: string): string {
    switch (level?.toLowerCase()) {
      case 'giallo':
        return 'orange-chips';
      case 'rosso':
        return 'red-chips';
      default:
        return 'green-chips';
    }
  }

  /**
   * Navigates to the full list/detail view of the widget.
   * Uses the path configured in widget.config.path.viewAll
   */
  onViewAll(): void {
    const path = this.widget?.config?.path?.viewAll;
    if (path) {
      this.router.navigateByUrl(`/home/${path}`);
    }
  }

  onChangeContext(): void {
    const endpoint = this.widget?.config?.path?.changeContext;
    if (!endpoint || this.isLoadingChangeContext()) return;

    this.isLoadingChangeContext.set(true);
    const url = `${environment.baseUrl}v1/${endpoint}`;
    const paramsMap = this.widget?.config?.path?.changeContextParams || {};
    const params = Object.entries(paramsMap).reduce(
      (acc, [key, value]) => acc.set(key, value),
      new HttpParams()
    );
    this.httpClient
      .get(url, { responseType: 'text', params })
      .pipe(take(1))
      .subscribe({
        next: (redirectUrl: string) => {
          this.isLoadingChangeContext.set(false);
          window.open(redirectUrl, '_blank');
        },
        error: () => {
          this.isLoadingChangeContext.set(false);
        },
      });
  }
}
